import { type CSSProperties, useRef } from "react";
import { blipPolar, heatOf, INNER, polarToBox } from "./radar";
import "./radar.css";

export interface RadarGuess {
  code: string;
  name: string;
  km: number;
  dir: string;
  pct: number;
}

const SWEEP_MS = 4200;
/** Distances labelled on the rings, in km. */
const RING_KM = [1000, 5000, 15000];

interface RadarScopeProps {
  /** Object URL of today's silhouette (null while loading). */
  shape: string | null;
  /** Oldest first, as the server keeps them. */
  guesses: RadarGuess[];
  status: "playing" | "won" | "lost";
  /** The answer's name once the game is over. */
  answer?: string | undefined;
}

/**
 * The scope. Rings are real distances (log scale), cardinal points are real
 * bearings; each guess blips where it lies relative to the hidden target at the
 * centre. Blips flare as the sweep passes them — each one's ping is phased to
 * the sweep once, when it first appears.
 */
export function RadarScope({ shape, guesses, status, answer }: RadarScopeProps) {
  const sweepStart = useRef(performance.now());
  const pingDelay = useRef(new Map<string, number>());
  const restored = useRef(guesses.length);
  // Win rings and static play only when the game ends in front of the player.
  const endedLive = useRef(false);
  const wasPlaying = useRef(status === "playing");
  if (wasPlaying.current && status !== "playing") endedLive.current = true;

  const misses = guesses.filter((g) => g.km > 0);
  const newest = misses[misses.length - 1]?.code;
  const closest = misses.reduce<RadarGuess | undefined>((best, g) => (!best || g.km < best.km ? g : best), undefined)?.code;

  const delayFor = (code: string, angle: number) => {
    const cached = pingDelay.current.get(code);
    if (cached !== undefined) return cached;
    const phase = (performance.now() - sweepStart.current) % SWEEP_MS;
    const d = ((angle / 360) * SWEEP_MS - phase + SWEEP_MS) % SWEEP_MS;
    pingDelay.current.set(code, d);
    return d;
  };

  const ringR = RING_KM.map((km) => blipPolar(km, "⬆️", 8).r);

  return (
    <div className={`rd rd-scope ${status === "won" ? "rd-won" : status === "lost" ? "rd-lost" : ""}`}>
      <span className="rd-cardinal" style={{ left: "50%", top: "4%" }}>ПН</span>
      <span className="rd-cardinal" style={{ left: "96%", top: "50%" }}>СХ</span>
      {status === "playing" && (
        <span className="rd-cardinal" style={{ left: "50%", top: "96%" }}>ПД</span>
      )}
      <span className="rd-cardinal" style={{ left: "4%", top: "50%" }}>ЗХ</span>

      <div className="rd-face" style={{ "--sweep-ms": `${SWEEP_MS}ms` } as CSSProperties}>
        <svg className="rd-grid" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r={INNER * 50} className="rd-ring-strong" strokeDasharray="1.2 1.6" />
          {ringR.map((r, i) => (
            <circle key={i} cx="50" cy="50" r={r * 50} />
          ))}
          <circle cx="50" cy="50" r={46.5} className="rd-ring-strong" />
          <line x1="50" y1="3.5" x2="50" y2={50 - INNER * 50} />
          <line x1="50" y1={50 + INNER * 50} x2="50" y2="96.5" />
          <line x1="3.5" y1="50" x2={50 - INNER * 50} y2="50" />
          <line x1={50 + INNER * 50} y1="50" x2="96.5" y2="50" />
          {RING_KM.map((km, i) => (
            <text key={km} x="51.2" y={50 - (ringR[i] ?? 0) * 50 - 0.8} fontSize="2.6" fill="rgba(196,236,248,0.4)">
              {km.toLocaleString("uk-UA")}
            </text>
          ))}
        </svg>

        <div className="rd-sweep" />

        <div className="rd-target-wrap">
          {shape && <div className="rd-target" style={{ "--shape": `url(${shape})` } as CSSProperties} />}
        </div>

        {misses.map((g, i) => {
          const { angle, r } = blipPolar(g.km, g.dir, i);
          const { x, y } = polarToBox(angle, r);
          const labelled = g.code === newest || g.code === closest;
          return (
            <div
              key={g.code}
              className={`rd-blip ${guesses.indexOf(g) >= restored.current ? "rd-new" : ""}`}
              data-heat={heatOf(g.pct)}
              style={{ left: `${x}%`, top: `${y}%`, "--ping-delay": `${delayFor(g.code, angle)}ms` } as CSSProperties}
            >
              <i className="rd-dot" />
              {labelled && <span className={`rd-label ${x < 50 ? "rd-label-r" : "rd-label-l"}`}>{g.name}</span>}
            </div>
          );
        })}

        {status === "won" && endedLive.current &&
          [0, 260, 520].map((d) => <div key={d} className="rd-lock" style={{ animationDelay: `${d}ms` }} />)}
        {status === "lost" && endedLive.current && <div className="rd-static" />}
      </div>

      {/* a label plate on the bezel's bottom edge — never covers a blip */}
      {status !== "playing" && (
        <div className="rd-verdict" role="status">
          {status === "won" ? "ЦІЛЬ ЗАХОПЛЕНО" : `ЦІЛЬ: ${(answer ?? "?").toUpperCase()}`}
        </div>
      )}
    </div>
  );
}
